'use client'

import '../../../slick-theme.css'
import 'slick-carousel/slick/slick.css'
import '../../../styles.css'

import {domAnimation, LazyMotion, MotionConfig} from 'framer-motion'
import {SessionProvider} from 'next-auth/react'
import {FC, PropsWithChildren, useEffect} from 'react'
import {QueryCache, QueryClient, QueryClientProvider} from 'react-query'
import {ReactQueryDevtools} from 'react-query/devtools'

import {DrawerProvider} from '@/components/common/drawer/context/DrawerContext'
import {GoogleTagManager} from '@/components/common/scripts/GoogleTagManager'
import {CampaignTracker} from '@/components/common/sideeffects/CampaignTracker'
import {ChoiceCookiesExpirationRefresh} from '@/components/common/sideeffects/ChoiceCookiesExpirationRefresh'
import SessionChecker from '@/components/common/sideeffects/SessionChecker'
import {SuccessModalProvider} from '@/components/common/success/context/SuccessModalContext'
import {DebugProvider} from '@/components/context/Debug'
import {CookiesBanner} from '@/components/cookies/CookiesBanner'
import {Missive} from '@/components/missive/Missive'
import {initAnalytics} from '@/utils/tracking/analytics'

import StyledJsxRegistry from '../../lib/registry'

// Module-level, so the cache survives a provider remount rather than being rebuilt.
const queryCache = new QueryCache()

const queryClient = new QueryClient({
  queryCache,
})

/**
 * Providers that need nothing from the request, so they can live above the point where static and
 * dynamic routes diverge and stay mounted while navigating between them.
 *
 * `SessionProvider` is the one that matters: its remount is the visible one, because
 * `useSession()` drops back to `'loading'` and the header's auth slot falls back to its
 * placeholder. Keeping it here is what stops that flash.
 *
 * A provider only belongs here if the component tree it *renders* is also request-free
 */
export const SharedProviders: FC<PropsWithChildren<unknown>> = ({children}) => {
  useEffect(() => {
    initAnalytics()

    const script = document.createElement('script')
    script.src = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'
    script.async = true
    document.body.appendChild(script)
  }, [])

  return (
    // `domAnimation` covers animations, exit animations, variants and hover/tap/focus gestures —
    // everything used here. It excludes drag and layout animations, which nothing uses. `strict`
    // makes a plain `motion.*` throw in development so the tree-shaking cannot silently regress.
    <LazyMotion features={domAnimation} strict>
      {/* With the OS "reduce motion" setting on, `m.*` skips transform and layout animations */}
      <MotionConfig reducedMotion="user">
        <SessionProvider refetchInterval={60}>
          <SessionChecker />
          <ChoiceCookiesExpirationRefresh />
          <CampaignTracker />
          <DebugProvider>
            <QueryClientProvider client={queryClient}>
              <DrawerProvider>
                <SuccessModalProvider>
                  <StyledJsxRegistry>
                    {/* Before the page so keyboard and screen reader users reach it first, not after
                      the footer. It is fixed to the bottom of the viewport, so this doesn't move it. */}
                    <CookiesBanner />
                    {children}
                  </StyledJsxRegistry>
                </SuccessModalProvider>
              </DrawerProvider>
              {/* Decides for itself whether the current path shows the chat, and only then loads the
                script. Needs nothing but the session, so it lives here and survives a group
                crossing — which its "stay mounted once a chat path is visited" latch relies on. */}
              <Missive />
              <ReactQueryDevtools initialIsOpen={false} />
              <GoogleTagManager />
            </QueryClientProvider>
          </DebugProvider>
        </SessionProvider>
      </MotionConfig>
    </LazyMotion>
  )
}
